import { cloneDeep } from "es-toolkit"
import type { Modifier, UpdateContext } from "~/types"
import { applyUpdate } from "~/utils/update/utils"
import migrateModifier_0_60_0 from "~/utils/update/migrations/0.60.0/migrateModifier"
import migrateModifier_0_61_0 from "~/utils/update/migrations/0.61.0/migrateModifier"
import migrateModifier_0_63_0 from "~/utils/update/migrations/0.63.0/migrateModifier"
import migrateModifier_0_64_0 from "~/utils/update/migrations/0.64.0/migrateModifier"
import migrateModifier_0_64_1 from "~/utils/update/migrations/0.64.1/migrateModifier"
import migrateModifier_0_65_0 from "~/utils/update/migrations/0.65.0/migrateModifier"
import migrateModifier_0_66_0 from "~/utils/update/migrations/0.66.0/migrateModifier"
import migrateModifier_0_71_0 from "~/utils/update/migrations/0.71.0/migrateModifier"
import migrateModifier_0_72_0 from "~/utils/update/migrations/0.72.0/migrateModifier"

/**
 * Applies version-specific migrations to a modifier object.
 * 
 * This function orchestrates the complete update pipeline for modifier objects,
 * applying all relevant migrations based on the update context. It ensures
 * modifiers remain compatible with the current application version by running
 * sequential migrations that transform data structures, fix bugs, or add new features.
 * 
 * The migration process:
 * 1. Creates a deep clone to avoid mutating the original
 * 2. Applies migrations in chronological order
 * 3. Handles special cases like re-applying specific migrations
 * 4. Returns the fully updated modifier
 * 
 * @param inputModifier - The original modifier to update
 * @param updateContext - Configuration controlling which migrations to apply
 * @returns A new modifier object with all applicable migrations applied
 * 
 * @example
 * ```typescript
 * const updatedModifier = updateModifier(oldModifier, {
 *   versionToUpdateFrom: "0.60.0",
 *   applyLatestUpdate: false,
 *   applyAllUpdates: false
 * });
 * // Applies migrations from 0.61.0 onwards
 * ```
 */
export default function updateModifier(
  inputModifier: Modifier,
  updateContext: UpdateContext
): Modifier {
  // Returns a processed clone of the input modifier
  // including an extra `updateStatus` property to track the modifier's update status.

  let modifier: Modifier = cloneDeep(inputModifier)

  // Run migrations.

  if (applyUpdate("0.60.0", updateContext)) {
    modifier = migrateModifier_0_60_0(modifier)
  }

  if (applyUpdate("0.61.0", updateContext)) {
    modifier = migrateModifier_0_61_0(modifier)
  }

  if (applyUpdate("0.63.0", updateContext)) {
    modifier = migrateModifier_0_63_0(modifier)
  }

  if (applyUpdate("0.64.0", updateContext)) {
    modifier = migrateModifier_0_64_0(modifier)
  }

  if (applyUpdate("0.64.1", updateContext)) {
    modifier = migrateModifier_0_64_1(modifier)
  }

  if (applyUpdate("0.64.2", updateContext)) {
    // Re-apply 0.64.1 migration.
    modifier = migrateModifier_0_64_1(modifier)
  }

  if (applyUpdate("0.65.0", updateContext)) {
    modifier = migrateModifier_0_65_0(modifier)
  }

  if (applyUpdate("0.66.0", updateContext)) {
    modifier = migrateModifier_0_66_0(modifier)
  }

  if (applyUpdate("0.71.0", updateContext)) {
    modifier = migrateModifier_0_71_0(modifier)
  }

  if (applyUpdate("0.72.0", updateContext)) {
    modifier = migrateModifier_0_72_0(modifier)
  }

  return modifier
}
